
import React, { Suspense, ComponentType, lazy } from 'react';
import { StandardizedLoadingState } from '@/components/ui/standardized-loading-state';

interface LazyLoadConfig {
  preload?: boolean;
  fallback?: React.ReactNode;
}

/**
 * Default loading fallback for lazy loaded components
 */
export const DefaultLoadingFallback = () => (
  <StandardizedLoadingState 
    message="Loading content..." 
    size="default" 
    centered={true} 
    context="lazy-load"
  />
);

/**
 * Enhanced lazy loading with preload option and standardized loading states
 */
export function lazyLoad<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  config?: LazyLoadConfig
): React.LazyExoticComponent<T> {
  const { preload = false, fallback } = config || {};
  
  // Create lazy component
  const LazyComponent = lazy(factory);
  
  // Preload if requested
  if (preload) {
    // Immediate preload
    factory().catch(err => 
      console.warn('Failed to preload component:', err)
    );
  }
  
  return LazyComponent;
}

interface LazyLoadWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Wrapper component for adding suspense boundaries without boilerplate
 */
export function LazyLoadWrapper({ children, fallback }: LazyLoadWrapperProps) {
  return (
    <Suspense fallback={fallback || <DefaultLoadingFallback />}>
      {children}
    </Suspense>
  );
}

// Directly export loading state in case it's referenced
export { StandardizedLoadingState as LoadingState } from '@/components/ui/standardized-loading-state';

export default {
  lazyLoad,
  LazyLoadWrapper,
  DefaultLoadingFallback
};
